"use client";

import React, { useEffect, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Info, ArrowLeft } from "lucide-react";
import EnableTwoFA from "./EnableTwoFA";
import { useAuth } from "../context/AuthContext";
import TwoFA from "./MainTwoFA";
import withOverlay from "../hoc/withOverlay";
import { SecuredFooter } from "./ui/secured-footer";
import Authentication from "../assets/images/Authentication.png";
import DownloadAuthAppButton from "../components/DownloadAuthAppButton";
import { CircleCheck } from "lucide-react";

const TwoFAOverlay = withOverlay(TwoFA);
const BankDetails = ({ bankInfo, onContinue, onBack, setUp }) => {
  const [open, setOpen] = useState(false);
  const [disableBtn, setDisableBtn] = useState(false);
  const [isTwoFAOpen, setIsTwoFAOpen] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const { settings } = useAuth();

  useEffect(() => {
    if (settings) setIsEnabled(settings?.security?.twoFactorAuth?.enabled);
  }, [settings]);

  const handleLocalBack = () => {
    if (onBack) {
      onBack();
    }
  };
  const handleConfirm = () => {
    setDisableBtn(true);
    if (!isEnabled) {
      setOpen(true);
    }
    if (isEnabled) {
      onContinue();
    }
  };

  const handleSetUp = () => {
    if (setUp) {
      setUp();
    }
  };
  const handleCloseOverlay = () => {
    setDisableBtn(false);
    setIsTwoFAOpen(false);
  };
  const handleClose = () => {
    setDisableBtn(false);
  };
  return (
    <>
      <Card className="w-full lg:pt-8 pt-5 !shadow-none bg-white rounded-2xl">
        <div className="lg:px-8 px-5">
          <div className="text-left px-4 lg:px-6 pt-6 lg:pt-8">
            <h3 className="text-lg lg:text-xl font-semibold text-[#6D6584]  mb-4 lg:mb-6">
              Ensure these details are correct
            </h3>
          </div>

          <CardContent className="px-2 lg:px-6 mt-4 lg:mt-6 w-full">
            <h3 className="text-sm font-semibold text-[#6D6584] mb-2">
              Bank account details
            </h3>
            <div className="border rounded-lg divide-y">
              <div className="p-3 lg:p-4 flex justify-between">
                <span className="text-sm text-[#6D6584]">Bank</span>
                <span className="text-sm font-medium text-[#6D6584] text-right">
                  {bankInfo?.bankName}
                </span>
              </div>

              <div className="p-3 lg:p-4 flex justify-between items-center">
                <span className="flex items-center gap-1 text-sm text-[#6D6584]">
                  Account Number
                  <Info size={14} className="text-[#6D6584]" />
                </span>
                <span className="text-sm font-medium text-[#6D6584]">
                  {bankInfo?.accNumber}
                </span>
              </div>

              <div className="p-3 lg:p-4 flex justify-between">
                <span className="text-sm text-[#6D6584]">Branch Code</span>
                <span className="text-sm font-bold text-[#6D6584]">
                  {bankInfo?.branchCode}
                </span>
              </div>
            </div>
            {!isEnabled ? (
              <div className="border-[1px] border-[#EBE9EF] p-4 sm:p-8 mb-6 rounded-2xl mt-8">
                <div className="flex flex-col sm:flex-row sm:items-center mb-4">
                  <img
                    src={Authentication}
                    alt="Authentication"
                    width={80}
                    height={60}
                    className="mb-3 sm:mb-0 sm:mr-4"
                  />
                  <div>
                    <p className="text-[#423E51] font-semibold text-[16px] mb-2">
                      2-Factor Authentication Not Active
                    </p>
                    <p className="text-[#6D6584] text-[14px]">
                      80eight requires 2-Factor Authentication in order to keep
                      your account secure. By using an authenticator app, you
                      ensure no one else can log in and transact using your
                      account.{" "}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-0 sm:justify-between">
                  <Button
                    className="bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] text-white px-4 sm:px-6 py-3 sm:py-4 rounded-full font-semibold hover:opacity-90 transition-opacity w-full sm:w-[49%] h-[48px]"
                    onClick={() => setOpen(true)}
                  >
                    Enable 2-Factor Authentication
                  </Button>
                  <DownloadAuthAppButton className="bg-transparent px-4 sm:px-6 py-3 sm:py-4 transition-opacity w-full sm:w-[49%] h-[48px]" />
                </div>
              </div>
            ) : (
              <div className="border-[1px] border-[#EBE9EF] p-4 rounded-2xl mt-8 bg-[#EBE9EF4D]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center  gap-2">
                    <img
                      src={Authentication}
                      alt="Authentication"
                      width={37}
                      height={25}
                      className="md:block hidden"
                    />
                    <CircleCheck className="text-[#17C3A5] font-normal block md:hidden" />
                    <p className="text-[#423E51] font-semibold text-[16px]">
                      2-Factor Authentication Active
                    </p>
                  </div>
                  <CircleCheck className="text-[#17C3A5] font-normal hidden md:block" />
                </div>
              </div>
            )}
          </CardContent>

          <div className="flex flex-col lg:flex-row justify-center items-center w-full px-4 lg:px-0 py-4 lg:py-6">
            <div className="lg:px-6 lg:py-6 md:flex items-center order-2 lg:order-1 hidden">
              <Button
                variant="ghost"
                onClick={handleLocalBack}
                className="w-10 h-10 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
              >
                <ArrowLeft size={20} className="text-gray-700" />
              </Button>
            </div>
            {isEnabled ? (
              <p className="text-[#6D6584] text-[14px] text-center md:hidden block order-2 lg:order-1">
                Use your own bank account. No cash deposits.
              </p>
            ) : (
              <Button
                variant="ghost"
                onClick={handleLocalBack}
                className="md:hidden block w-full h-[48px] rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100 order-2 lg:order-1 mt-2"
              >
                Back
              </Button>
            )}

            <div className="flex items-center justify-center w-full lg:w-auto mb-2 lg:mb-0 order-1 lg:order-2">
              <Button
                onClick={handleConfirm}
                disabled={!isEnabled || disableBtn}
                className={`px-6 py-4 rounded-full font-semibold transition-opacity w-full sm:min-w-[302px] max-w-sm h-[48px] text-sm sm:text-base ${
                  !isEnabled || disableBtn
                    ? "bg-[#F2F1F4] text-[#9D96AF] cursor-not-allowed"
                    : "bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] text-white hover:opacity-90"
                }`}
              >
                Continue
              </Button>
            </div>
          </div>

          <EnableTwoFA
            open={open}
            setOpen={setOpen}
            setIsTwoFAOpen={setIsTwoFAOpen}
            onClose={handleClose}
          />
        </div>
        <SecuredFooter />
      </Card>
      <TwoFAOverlay
        isOpen={isTwoFAOpen}
        closeOverlay={handleCloseOverlay}
        isSetUp={handleSetUp}
        className="max-w-[60%]"
      />
    </>
  );
};

export default BankDetails;
